/**
 * usePanelWidth — ширина растягиваемой левой панели фасетов (258–560px).
 *
 * Значение живёт в localStorage (ключ mobui-facets-width) и переживает F5;
 * невалидное/отсутствующее значение → 258. startResize вешается на
 * onPointerDown ручки-разделителя: на время drag на window ставятся
 * pointermove/pointerup-слушатели (width = clamp(startWidth + dx)), курсор
 * body переключается в col-resize и выключается выделение текста; на
 * pointerup всё снимается и ширина сохраняется. resetWidth (двойной клик по
 * ручке) возвращает 258px и тоже сохраняет.
 */

import * as React from "react";

const STORAGE_KEY = "mobui-facets-width";
const DEFAULT = 258;
const MIN = 258;
const MAX = 560;

export interface UsePanelWidthOptions {
  /** Ключ localStorage (по умолчанию mobui-facets-width). */
  storageKey?: string;
  /** Ширина по умолчанию и цель resetWidth (по умолчанию 258). */
  defaultWidth?: number;
  /** Минимальная ширина (по умолчанию 258). */
  min?: number;
  /** Максимальная ширина (по умолчанию 560). */
  max?: number;
}

export interface UsePanelWidthResult {
  /** Текущая ширина панели, px (всегда в пределах [min, max]). */
  width: number;
  /** Повесить на onPointerDown ручки-разделителя. */
  startResize: (e: React.PointerEvent) => void;
  /** Сброс к ширине по умолчанию (повесить на onDoubleClick ручки). */
  resetWidth: () => void;
}

function clampTo(min: number, max: number, w: number): number {
  return Math.min(max, Math.max(min, w));
}

/** Прочитать сохранённую ширину; невалидное значение (NaN и т.п.) → дефолт. */
function readStored(key: string, min: number, max: number, fallback: number): number {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null || raw.trim() === "") return fallback;
    const n = Number(raw);
    if (!Number.isFinite(n)) return fallback;
    return clampTo(min, max, Math.round(n));
  } catch {
    // localStorage недоступен (private mode и т.п.) — просто дефолт.
    return fallback;
  }
}

function persist(key: string, w: number): void {
  try {
    window.localStorage.setItem(key, String(w));
  } catch {
    // Некритично: ширина не сохранится между перезагрузками.
  }
}

export function usePanelWidth(opts?: UsePanelWidthOptions): UsePanelWidthResult {
  const storageKey = opts?.storageKey ?? STORAGE_KEY;
  const defaultWidth = opts?.defaultWidth ?? DEFAULT;
  const min = opts?.min ?? MIN;
  const max = opts?.max ?? MAX;

  const [width, setWidth] = React.useState<number>(() =>
    readStored(storageKey, min, max, defaultWidth),
  );

  // Актуальная ширина для startResize без пересоздания колбэка на каждый пиксель.
  const widthRef = React.useRef(width);
  widthRef.current = width;

  const startResize = React.useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = widthRef.current;
      const target = e.currentTarget as Element;
      const pointerId = e.pointerId;
      let lastWidth = startWidth;

      const prevCursor = document.body.style.cursor;
      const prevUserSelect = document.body.style.userSelect;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      // Capture the pointer so moves keep coming even if it leaves the handle.
      try {
        target.setPointerCapture?.(pointerId);
      } catch {
        /* not supported / already released — degrade gracefully */
      }

      // Single teardown — runs on pointerup AND pointercancel, so an interrupted
      // gesture never leaves listeners, col-resize cursor or user-select:none stuck (F46).
      const cleanup = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onEnd);
        window.removeEventListener("pointercancel", onEnd);
        document.body.style.cursor = prevCursor;
        document.body.style.userSelect = prevUserSelect;
        try {
          target.releasePointerCapture?.(pointerId);
        } catch {
          /* already released */
        }
      };
      const onMove = (ev: PointerEvent) => {
        lastWidth = clampTo(min, max, startWidth + (ev.clientX - startX));
        setWidth(lastWidth);
      };
      const onEnd = () => {
        cleanup();
        persist(storageKey, lastWidth);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onEnd);
      window.addEventListener("pointercancel", onEnd);
    },
    [storageKey, min, max],
  );

  const resetWidth = React.useCallback(() => {
    setWidth(defaultWidth);
    persist(storageKey, defaultWidth);
  }, [storageKey, defaultWidth]);

  return { width, startResize, resetWidth };
}
